import { CompositionEventsEnum } from "../CompositionEvents";
import { CompositionService } from "./CompositionService";
import type { ResolveAssetUrlFn } from "./CompositionSessionService";
import { Input, UrlSource, ALL_FORMATS } from "mediabunny";
import type { MediaSourceRef } from "../../engine/sources/media-source";
import { VideoClip } from "../../engine/clips/video/video";
import { AudioClip } from "../../engine/clips";
import { ImageClip } from "../../engine/clips/image/image";
import { ScriptLayerClip } from "../../engine/clips/script/script";
import type { ScriptLayerInputConfig } from "../../engine/clips/script/script.interfaces";
import { Timestamp } from "../../engine/models";
import { EngineInternalEventsEnum } from "../../engine/EngineInternalEvents";
import { CompositionStateStore } from "../store/CompositionStateStore";
import {
    ContributionCapture,
    createCaptureId,
    type ContributionCommittedOperationInputV1,
    type ContributionJsonValue,
    type ContributionOperationTargetRef,
} from "../../instrumentation/contribution";

type ClipSnapshot = {
    id: string;
    type: string;
    layerId?: string;
    assetId?: string | null;
    thumbnailId?: string | null;
    name?: string;
    inSec?: number;
    outSec?: number;
    offsetSec?: number;
    props?: { gain?: number; playbackRate?: number; alpha?: number };
    visual?: {
        x?: number;
        y?: number;
        width?: number;
        height?: number;
        rotation?: number;
        alpha?: number;
        scaleX?: number;
        scaleY?: number;
        crop?: { x: number; y: number; width: number; height: number };
    };
    // ScriptLayerClip-specific
    scriptCuts?: any[];
    gaps?: any[];
    gapInsertions?: any[];
    splits?: any[];
    speakerOverrides?: any[];
    speakerMuteRanges?: any[];
    transcriptHiddenRanges?: any[];
    wordCorrections?: any[];
    transcriptParagraphBreaks?: any[];
    transcriptMarkers?: any[];
    sceneStyleOverrides?: Record<string, any>;
    sceneVisualOverrides?: Record<string, any>;
    sceneVisualOverridesByAspect?: Record<string, any>;
    sceneProperties?: any[];
    multicamCameraSetup?: any | null;
    sources?: Array<{
        assetId: string;
        kind?: "audio" | "video";
        name?: string;
        color?: string;
        gain: number;
        playbackRate: number;
        offsetSec: number;
        range?: { inSec: number; outSec: number };
        segments?: Array<{
            id?: string;
            offsetSec: number;
            playbackRate?: number;
            range: { inSec: number; outSec: number };
        }>;
        visual?: {
            x: number;
            y: number;
            width: number;
            height: number;
            alpha: number;
            crop?: { x: number; y: number; width: number; height: number };
        };
        layerIndex?: number;
    }>;
};

type UndoEntry =
    | { kind: "clip:update"; clipId: string; before: ClipSnapshot; after: ClipSnapshot; at: number }
    | { kind: "clip:add"; clip: ClipSnapshot; at: number }
    | { kind: "clip:remove"; clip: ClipSnapshot; at: number }
    | { kind: "script:edits"; clipId: string; before: Pick<ClipSnapshot, "scriptCuts" | "gaps" | "gapInsertions" | "splits" | "speakerOverrides" | "speakerMuteRanges" | "transcriptHiddenRanges" | "wordCorrections" | "transcriptParagraphBreaks" | "transcriptMarkers">; after: Pick<ClipSnapshot, "scriptCuts" | "gaps" | "gapInsertions" | "splits" | "speakerOverrides" | "speakerMuteRanges" | "transcriptHiddenRanges" | "wordCorrections" | "transcriptParagraphBreaks" | "transcriptMarkers">; at: number }
    | { kind: "canvas:resize"; before: { width: number; height: number }; after: { width: number; height: number }; at: number }
    | { kind: "canvas:bg"; before: string; after: string; at: number }
    | { kind: "batch"; entries: UndoEntry[]; at: number };

const CONTRIBUTION_OPERATION_SETTLE_MS = 450;

const contributionJson = (value: unknown): ContributionJsonValue | undefined => {
    try {
        const encoded = JSON.stringify(value);
        if (encoded === undefined) return undefined;
        return JSON.parse(encoded) as ContributionJsonValue;
    } catch {
        return undefined;
    }
};

const operationTarget = (
    entityType: ContributionOperationTargetRef["entityType"],
    entityId: unknown,
): ContributionOperationTargetRef | null => {
    const normalizedId = String(entityId ?? "").trim();
    return normalizedId ? { entityType, entityId: normalizedId } : null;
};

/**
 * Keep contribution payloads bounded and content-light. These snapshots describe
 * the semantic editor mutation without copying transcript text, filenames, URLs,
 * or the complete ScriptLayerClip into the telemetry queue.
 */
const contributionClipSnapshot = (snapshot: ClipSnapshot): ContributionJsonValue => {
    const scriptFields = {
        scriptCuts: snapshot.scriptCuts,
        gaps: snapshot.gaps,
        gapInsertions: snapshot.gapInsertions,
        splits: snapshot.splits,
        speakerOverrides: snapshot.speakerOverrides,
        speakerMuteRanges: snapshot.speakerMuteRanges,
        transcriptHiddenRanges: snapshot.transcriptHiddenRanges,
        transcriptParagraphBreaks: snapshot.transcriptParagraphBreaks,
    };
    const scriptCounts = Object.fromEntries(
        Object.entries(scriptFields).map(([key, rows]) => [key, Array.isArray(rows) ? rows.length : 0]),
    );
    const value = {
        clipId: snapshot.id,
        type: snapshot.type,
        layerId: snapshot.layerId,
        assetId: snapshot.assetId,
        thumbnailId: snapshot.thumbnailId,
        timing: {
            inSec: snapshot.inSec,
            outSec: snapshot.outSec,
            offsetSec: snapshot.offsetSec,
        },
        props: snapshot.props,
        visual: snapshot.visual,
        scriptStructureCounts: scriptCounts,
        sourceMappings: (snapshot.sources ?? []).slice(0, 64).map((source) => ({
            assetId: source.assetId,
            kind: source.kind,
            gain: source.gain,
            playbackRate: source.playbackRate,
            offsetSec: source.offsetSec,
            range: source.range,
            segments: (source.segments ?? []).slice(0, 256).map((segment) => ({
                id: segment.id,
                offsetSec: segment.offsetSec,
                playbackRate: segment.playbackRate,
                range: segment.range,
            })),
            visual: source.visual,
            layerIndex: source.layerIndex,
        })),
        scenePropertyCount: snapshot.sceneProperties?.length ?? 0,
        hasMulticamSetup: Boolean(snapshot.multicamCameraSetup),
    };
    return contributionJson(value) ?? null;
};

const contributionScriptState = (
    value: Pick<ClipSnapshot, "scriptCuts" | "gaps" | "gapInsertions" | "splits" | "speakerOverrides" | "speakerMuteRanges" | "transcriptHiddenRanges" | "wordCorrections" | "transcriptParagraphBreaks" | "transcriptMarkers">,
): ContributionJsonValue => contributionJson({
    scriptCuts: (value.scriptCuts ?? []).slice(0, 256),
    scriptCutCount: value.scriptCuts?.length ?? 0,
    gaps: (value.gaps ?? []).slice(0, 256),
    gapCount: value.gaps?.length ?? 0,
    gapInsertions: (value.gapInsertions ?? []).slice(0, 256),
    gapInsertionCount: value.gapInsertions?.length ?? 0,
    splits: (value.splits ?? []).slice(0, 256),
    splitCount: value.splits?.length ?? 0,
    speakerOverrides: (value.speakerOverrides ?? []).slice(0, 256),
    speakerOverrideCount: value.speakerOverrides?.length ?? 0,
    speakerMuteRanges: (value.speakerMuteRanges ?? []).slice(0, 256),
    speakerMuteRangeCount: value.speakerMuteRanges?.length ?? 0,
    transcriptHiddenRanges: (value.transcriptHiddenRanges ?? []).slice(0, 256),
    transcriptHiddenRangeCount: value.transcriptHiddenRanges?.length ?? 0,
    transcriptParagraphBreaks: (value.transcriptParagraphBreaks ?? []).slice(0, 256),
    transcriptParagraphBreakCount: value.transcriptParagraphBreaks?.length ?? 0,
    // Word corrections and marker labels may contain transcript/project content.
    wordCorrectionCount: value.wordCorrections?.length ?? 0,
    transcriptMarkerCount: value.transcriptMarkers?.length ?? 0,
}) ?? null;

const shallowEqualJson = (a: any, b: any) => {
    try {
        return JSON.stringify(a) === JSON.stringify(b);
    } catch {
        return false;
    }
};

const cloneSnapshotValue = <T,>(value: T): T => {
    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        if (value && typeof value === "object") {
            return { ...(value as any) } as T;
        }
        return value;
    }
};

const cloneSnapshotArray = (value: unknown): any[] => {
    if (!Array.isArray(value)) return [];
    return cloneSnapshotValue(value);
};

/**
 * In-memory undo/redo for a single editor session.
 * Does not persist across reloads.
 */
export class UndoManager {
    private static initialized = false;
    private static applying = false;
    private static resolveAssetUrl: ResolveAssetUrlFn | null = null;

    private static undoStack: UndoEntry[] = [];
    private static redoStack: UndoEntry[] = [];
    private static transactionDepth = 0;
    private static transactionEntries: UndoEntry[] = [];
    private static transactionCheckpoints: Array<{
        entryStart: number;
        composition: any | null;
        clips: Map<string, ClipSnapshot>;
        canvas: { width: number; height: number; background: string };
    }> = [];

    private static contributionTimers = new Map<UndoEntry, ReturnType<typeof setTimeout>>();
    private static contributionOperationIds = new WeakMap<object, string>();
    private static contributionEmitted = new WeakSet<object>();
    private static contributionEntryInFlight = new WeakMap<object, Promise<void>>();
    private static contributionInFlight = new Set<Promise<void>>();

    private static listeners = new Set<() => void>();

    private static clipShadow = new Map<string, ClipSnapshot>();
    private static scriptShadow = new Map<
        string,
        Pick<ClipSnapshot, "scriptCuts" | "gaps" | "gapInsertions" | "splits" | "speakerOverrides" | "speakerMuteRanges" | "transcriptHiddenRanges" | "wordCorrections" | "transcriptParagraphBreaks" | "transcriptMarkers">
    >();

    private static canvasShadow: { width: number; height: number; background: string } = {
        width: 1920,
        height: 1080,
        background: "#000000",
    };

    static get canUndo(): boolean {
        return this.undoStack.length > 0;
    }

    static get canRedo(): boolean {
        return this.redoStack.length > 0;
    }

    static init(args?: { resolveAssetUrl?: ResolveAssetUrlFn }): void {
        if (args?.resolveAssetUrl) this.resolveAssetUrl = args.resolveAssetUrl;
        if (!CompositionService.isInitialized()) return;

        // Reset stacks per composition session.
        this.undoStack = [];
        this.redoStack = [];
        this.transactionDepth = 0;
        this.transactionEntries = [];
        this.transactionCheckpoints = [];
        this.clipShadow.clear();
        this.scriptShadow.clear();

        // Seed shadow state from current engine composition.
        const comp: any = CompositionService.composition;
        this.canvasShadow = {
            width: Number(comp?.settings?.width ?? 1920),
            height: Number(comp?.settings?.height ?? 1080),
            background: String(comp?.settings?.background ?? "#000000"),
        };
        for (const tr of comp?.tracks ?? []) {
            for (const clip of tr?.clips ?? []) {
                const snap = this.snapshotClip(clip);
                this.clipShadow.set(snap.id, snap);
                if (snap.type === "script") {
                    this.scriptShadow.set(snap.id, {
                        scriptCuts: snap.scriptCuts ?? [],
                        gaps: snap.gaps ?? [],
                        gapInsertions: snap.gapInsertions ?? [],
                        splits: snap.splits ?? [],
                        speakerOverrides: snap.speakerOverrides ?? [],
                        speakerMuteRanges: snap.speakerMuteRanges ?? [],
                        transcriptHiddenRanges: snap.transcriptHiddenRanges ?? [],
                        wordCorrections: snap.wordCorrections ?? [],
                        transcriptParagraphBreaks: snap.transcriptParagraphBreaks ?? [],
                        transcriptMarkers: snap.transcriptMarkers ?? [],
                    });
                }
            }
        }

        // Bind listeners.
        if (this.initialized) this.teardown();
        comp.on(CompositionEventsEnum.CLIP_ADDED, this.handleClipAdded);
        comp.on(CompositionEventsEnum.CLIP_REMOVED, this.handleClipRemoved);
        comp.on(CompositionEventsEnum.CLIP_UPDATED, this.handleClipUpdated);
        comp.on(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, this.handleScriptLayerUpdated);
        comp.on(CompositionEventsEnum.CANVAS_RESIZED, this.handleCanvasResized);
        comp.on(CompositionEventsEnum.CANVAS_BACKGROUND_CHANGED, this.handleCanvasBackgroundChanged);
        this.initialized = true;
        this.emitChange();
    }

    static teardown(): void {
        if (!this.initialized) return;
        try {
            const comp: any = CompositionService.composition;
            comp.off?.(CompositionEventsEnum.CLIP_ADDED, this.handleClipAdded);
            comp.off?.(CompositionEventsEnum.CLIP_REMOVED, this.handleClipRemoved);
            comp.off?.(CompositionEventsEnum.CLIP_UPDATED, this.handleClipUpdated);
            comp.off?.(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, this.handleScriptLayerUpdated);
            comp.off?.(CompositionEventsEnum.CANVAS_RESIZED, this.handleCanvasResized);
            comp.off?.(CompositionEventsEnum.CANVAS_BACKGROUND_CHANGED, this.handleCanvasBackgroundChanged);
        } catch {}
        this.initialized = false;
        this.undoStack = [];
        this.redoStack = [];
        this.transactionDepth = 0;
        this.transactionEntries = [];
        this.transactionCheckpoints = [];
        for (const timer of this.contributionTimers.values()) clearTimeout(timer);
        this.contributionTimers.clear();
        this.contributionOperationIds = new WeakMap<object, string>();
        this.contributionEmitted = new WeakSet<object>();
        this.contributionEntryInFlight = new WeakMap<object, Promise<void>>();
        this.contributionInFlight.clear();
        this.clipShadow.clear();
        this.scriptShadow.clear();
        this.emitChange();
    }

    static beginTransaction(): void {
        const clips = new Map<string, ClipSnapshot>();
        let checkpointComposition: any | null = null;
        try {
            const comp: any = CompositionService.composition;
            checkpointComposition = comp;
            for (const track of comp?.tracks ?? []) {
                for (const clip of track?.clips ?? []) {
                    const snap = this.snapshotClip(clip);
                    if (snap.id) clips.set(snap.id, snap);
                }
            }
        } catch {}

        let canvas = { ...this.canvasShadow };
        try {
            const comp: any = CompositionService.composition;
            canvas = {
                width: Number(comp?.settings?.width ?? comp?.width ?? canvas.width),
                height: Number(comp?.settings?.height ?? comp?.height ?? canvas.height),
                background: String(comp?.settings?.background ?? canvas.background),
            };
        } catch {}

        this.transactionCheckpoints.push({
            entryStart: this.transactionEntries.length,
            composition: checkpointComposition,
            clips,
            canvas,
        });
        this.transactionDepth += 1;
        if (this.transactionDepth === 1) {
            this.transactionEntries = [];
            const checkpoint = this.transactionCheckpoints[this.transactionCheckpoints.length - 1];
            if (checkpoint) checkpoint.entryStart = 0;
        }
    }

    static endTransaction(): void {
        if (this.transactionDepth <= 0) return;
        this.transactionCheckpoints.pop();
        this.transactionDepth -= 1;
        if (this.transactionDepth > 0) return;

        const entries = this.transactionEntries;
        this.transactionEntries = [];
        if (entries.length === 0) return;

        this.redoStack = [];
        let committedEntry: UndoEntry;
        if (entries.length === 1) {
            if (!this.coalesceIntoStack(this.undoStack, entries[0])) {
                this.undoStack.push(entries[0]);
            }
            committedEntry = this.undoStack[this.undoStack.length - 1];
        } else {
            committedEntry = {
                kind: "batch",
                entries: [...entries],
                at: Number(entries[entries.length - 1]?.at ?? Date.now()),
            };
            this.undoStack.push(committedEntry);
        }
        this.scheduleContributionOperation(committedEntry);
        this.emitChange();
    }

    /**
     * Roll back the innermost transaction to the state captured by beginTransaction().
     * The failed work is discarded rather than being added to undo/redo history.
     */
    static async abortTransaction(): Promise<void> {
        if (this.transactionDepth <= 0) return;

        const checkpoint = this.transactionCheckpoints.pop();
        const entryStart = Math.max(0, Number(checkpoint?.entryStart ?? 0));
        this.transactionEntries.splice(entryStart);
        this.transactionDepth -= 1;

        if (!checkpoint) {
            if (this.transactionDepth === 0) this.transactionEntries = [];
            return;
        }

        // A detached composition must never be restored into a newly opened session.
        try {
            if (CompositionService.composition !== checkpoint.composition) {
                if (this.transactionDepth === 0) this.transactionEntries = [];
                this.emitChange();
                return;
            }
        } catch {
            if (this.transactionDepth === 0) this.transactionEntries = [];
            this.emitChange();
            return;
        }

        const wasApplying = this.applying;
        this.applying = true;
        try {
            const currentIds = new Set<string>();
            try {
                const comp: any = CompositionService.composition;
                for (const track of comp?.tracks ?? []) {
                    for (const clip of track?.clips ?? []) {
                        const id = String(clip?.id ?? "");
                        if (id) currentIds.add(id);
                    }
                }
            } catch {}

            // Remove clips created inside the failed transaction.
            for (const id of currentIds) {
                if (!checkpoint.clips.has(id)) {
                    try { CompositionService.deleteClipById(id); } catch {}
                    try { CompositionStateStore.removeMetadataForClip(id); } catch {}
                }
            }

            // Restore existing clips and recreate any removed by the failed transaction.
            for (const [id, snap] of checkpoint.clips) {
                if (CompositionService.getClipById(id)) {
                    await this.applySnapshotToExistingClip(id, snap);
                } else {
                    await this.recreateClip(snap);
                }
            }

            try {
                const comp: any = CompositionService.composition;
                const currentWidth = Number(comp?.settings?.width ?? comp?.width ?? 0);
                const currentHeight = Number(comp?.settings?.height ?? comp?.height ?? 0);
                if (
                    currentWidth !== checkpoint.canvas.width ||
                    currentHeight !== checkpoint.canvas.height
                ) {
                    comp.resize(checkpoint.canvas.width, checkpoint.canvas.height);
                }
                const currentBackground = String(
                    comp?.settings?.background ?? this.canvasShadow.background,
                );
                if (currentBackground !== checkpoint.canvas.background) {
                    comp.changeBackground(checkpoint.canvas.background as any);
                }
            } catch {}
            try {
                await (CompositionService.composition as any)?.onClipTimingChanged?.();
            } catch {}
        } finally {
            this.applying = wasApplying;
        }

        // Re-seed shadows from the restored live state so the batch flush that follows
        // cannot turn a rollback into a new undo entry.
        this.clipShadow.clear();
        this.scriptShadow.clear();
        try {
            const comp: any = CompositionService.composition;
            for (const track of comp?.tracks ?? []) {
                for (const clip of track?.clips ?? []) {
                    const snap = this.snapshotClip(clip);
                    this.clipShadow.set(snap.id, snap);
                    if (snap.type === "script") {
                        this.scriptShadow.set(snap.id, {
                            scriptCuts: snap.scriptCuts ?? [],
                            gaps: snap.gaps ?? [],
                            gapInsertions: snap.gapInsertions ?? [],
                            splits: snap.splits ?? [],
                            speakerOverrides: snap.speakerOverrides ?? [],
                            speakerMuteRanges: snap.speakerMuteRanges ?? [],
                            transcriptHiddenRanges: snap.transcriptHiddenRanges ?? [],
                            wordCorrections: snap.wordCorrections ?? [],
                            transcriptParagraphBreaks: snap.transcriptParagraphBreaks ?? [],
                            transcriptMarkers: snap.transcriptMarkers ?? [],
                        });
                    }
                }
            }
        } catch {}
        this.canvasShadow = { ...checkpoint.canvas };

        if (this.transactionDepth === 0) this.transactionEntries = [];
        this.emitChange();
    }

    static async undo(): Promise<void> {
        const entry = this.undoStack.pop();
        if (!entry) return;
        const contributionReady = this.flushContributionEntry(entry);
        this.applying = true;
        try {
            await this.applyInverse(entry);
            this.redoStack.push(entry);
        } finally {
            this.applying = false;
        }
        const reversedOperationId = this.contributionOperationIds.get(entry as object);
        void contributionReady.then(() => ContributionCapture.recordOperation({
                operationKind: "history.undo",
                targets: this.contributionTargets(entry),
                ...(reversedOperationId ? { reversesOperationId: reversedOperationId } : {}),
                details: { originalKind: entry.kind },
                origin: "human",
            }))
            .catch(() => {});
        this.emitChange();
    }

    static async redo(): Promise<void> {
        const entry = this.redoStack.pop();
        if (!entry) return;
        const contributionReady = this.flushContributionEntry(entry);
        this.applying = true;
        try {
            await this.applyForward(entry);
            this.undoStack.push(entry);
        } finally {
            this.applying = false;
        }
        const reappliedOperationId = this.contributionOperationIds.get(entry as object);
        void contributionReady.then(() => ContributionCapture.recordOperation({
                operationKind: "history.redo",
                targets: this.contributionTargets(entry),
                ...(reappliedOperationId ? { reappliesOperationId: reappliedOperationId } : {}),
                details: { originalKind: entry.kind },
                origin: "human",
            }))
            .catch(() => {});
        this.emitChange();
    }

    /** Flush debounced committed edits before a composition session is closed. */
    static async flushContributionOperations(): Promise<void> {
        const pendingEntries = Array.from(this.contributionTimers.keys());
        for (const entry of pendingEntries) {
            await this.flushContributionEntry(entry);
        }
        if (this.contributionInFlight.size > 0) {
            await Promise.allSettled(Array.from(this.contributionInFlight));
        }
    }

    static subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private static emitChange(): void {
        for (const fn of this.listeners) {
            try { fn(); } catch {}
        }
    }

    private static coalesceIntoStack(stack: UndoEntry[], entry: UndoEntry): boolean {
        const now = Number(entry.at);
        const last = stack[stack.length - 1];
        const COALESCE_MS = 400;
        if (entry.kind === "clip:update" && last?.kind === "clip:update") {
            if (last.clipId === entry.clipId && now - last.at < COALESCE_MS) {
                (last as any).after = entry.after;
                (last as any).at = now;
                return true;
            }
        }
        if (entry.kind === "canvas:resize" && last?.kind === "canvas:resize") {
            if (now - last.at < COALESCE_MS) {
                (last as any).after = entry.after;
                (last as any).at = now;
                return true;
            }
        }
        if (entry.kind === "canvas:bg" && last?.kind === "canvas:bg") {
            if (now - last.at < COALESCE_MS) {
                (last as any).after = entry.after;
                (last as any).at = now;
                return true;
            }
        }
        if (entry.kind === "script:edits" && last?.kind === "script:edits") {
            if (last.clipId === entry.clipId && now - last.at < COALESCE_MS) {
                (last as any).after = entry.after;
                (last as any).at = now;
                return true;
            }
        }
        return false;
    }

    private static record(entry: UndoEntry): void {
        if (this.transactionDepth > 0) {
            if (!this.coalesceIntoStack(this.transactionEntries, entry)) {
                this.transactionEntries.push(entry);
            }
            return;
        }

        // New edits clear redo history.
        this.redoStack = [];

        if (this.coalesceIntoStack(this.undoStack, entry)) {
            const committedEntry = this.undoStack[this.undoStack.length - 1];
            if (committedEntry) this.scheduleContributionOperation(committedEntry);
            this.emitChange();
            return;
        }

        this.undoStack.push(entry);
        this.scheduleContributionOperation(entry);
        this.emitChange();
    }

    private static scheduleContributionOperation(entry: UndoEntry): void {
        if (!ContributionCapture.getActiveSessionContext()) return;
        if (!this.contributionOperationIds.has(entry as object)) {
            this.contributionOperationIds.set(entry as object, createCaptureId("operation"));
        }
        const previousTimer = this.contributionTimers.get(entry);
        if (previousTimer) clearTimeout(previousTimer);
        const timer = setTimeout(() => {
            this.contributionTimers.delete(entry);
            void this.startContributionEntryEmission(entry);
        }, CONTRIBUTION_OPERATION_SETTLE_MS);
        this.contributionTimers.set(entry, timer);
    }

    private static async flushContributionEntry(entry: UndoEntry): Promise<void> {
        if (!this.contributionOperationIds.has(entry as object)) return;
        const timer = this.contributionTimers.get(entry);
        if (timer) {
            clearTimeout(timer);
            this.contributionTimers.delete(entry);
            await this.startContributionEntryEmission(entry);
            return;
        }
        const current = this.contributionEntryInFlight.get(entry as object);
        if (current) {
            await current;
            return;
        }
        if (this.contributionEmitted.has(entry as object)) return;
        await this.startContributionEntryEmission(entry);
    }

    private static startContributionEntryEmission(entry: UndoEntry): Promise<void> {
        const current = this.contributionEntryInFlight.get(entry as object);
        if (current) return current;
        if (!this.contributionOperationIds.has(entry as object)) return Promise.resolve();
        if (this.contributionEmitted.has(entry as object)) return Promise.resolve();
        const promise = this.emitContributionEntry(entry).finally(() => {
            this.contributionEmitted.add(entry as object);
            this.contributionEntryInFlight.delete(entry as object);
            this.contributionInFlight.delete(promise);
        });
        this.contributionEntryInFlight.set(entry as object, promise);
        this.contributionInFlight.add(promise);
        return promise;
    }

    private static contributionTargets(entry: UndoEntry): ContributionOperationTargetRef[] {
        if (entry.kind === "batch") {
            const seen = new Set<string>();
            const targets: ContributionOperationTargetRef[] = [];
            for (const child of entry.entries) {
                for (const target of this.contributionTargets(child)) {
                    const key = `${target.entityType}:${target.entityId}`;
                    if (seen.has(key)) continue;
                    seen.add(key);
                    targets.push(target);
                }
            }
            return targets;
        }
        if (entry.kind === "clip:add" || entry.kind === "clip:remove") {
            return [operationTarget(entry.clip.type === "script" ? "script" : "clip", entry.clip.id)]
                .filter((target): target is ContributionOperationTargetRef => Boolean(target));
        }
        if (entry.kind === "clip:update") {
            return [operationTarget(entry.after.type === "script" ? "script" : "clip", entry.clipId)]
                .filter((target): target is ContributionOperationTargetRef => Boolean(target));
        }
        if (entry.kind === "script:edits") {
            return [operationTarget("script", entry.clipId)]
                .filter((target): target is ContributionOperationTargetRef => Boolean(target));
        }
        return [{ entityType: "canvas", entityId: "canvas" }];
    }

    private static contributionInputForEntry(
        entry: Exclude<UndoEntry, { kind: "batch" }>,
        transactionId?: string,
    ): ContributionCommittedOperationInputV1 {
        const shared = {
            ...(transactionId ? { transactionId } : {}),
            targets: this.contributionTargets(entry),
            origin: "human" as const,
            details: {
                capturedFrom: "undo_manager",
                completeness: "sanitized_structure",
            },
        };
        switch (entry.kind) {
        case "clip:add":
            return {
                ...shared,
                operationKind: "clip.add",
                before: null,
                after: contributionClipSnapshot(entry.clip),
            };
        case "clip:remove":
            return {
                ...shared,
                operationKind: "clip.remove",
                before: contributionClipSnapshot(entry.clip),
                after: null,
            };
        case "clip:update":
            return {
                ...shared,
                operationKind: "clip.update",
                before: contributionClipSnapshot(entry.before),
                after: contributionClipSnapshot(entry.after),
            };
        case "script:edits":
            return {
                ...shared,
                operationKind: "script.edit",
                before: contributionScriptState(entry.before),
                after: contributionScriptState(entry.after),
            };
        case "canvas:resize":
            return {
                ...shared,
                operationKind: "canvas.resize",
                before: contributionJson(entry.before),
                after: contributionJson(entry.after),
            };
        case "canvas:bg":
            return {
                ...shared,
                operationKind: "canvas.background",
                before: entry.before,
                after: entry.after,
            };
        }
    }

    private static async emitContributionEntry(entry: UndoEntry): Promise<void> {
        try {
            if (entry.kind !== "batch") {
                const operationId = this.contributionOperationIds.get(entry as object);
                if (!operationId) return;
                await ContributionCapture.recordOperation({
                    ...this.contributionInputForEntry(entry),
                    operationId,
                });
                return;
            }

            const transactionId = createCaptureId("transaction");
            const childOperationIds: string[] = [];
            for (const child of entry.entries) {
                if (child.kind === "batch") continue;
                const childOperationId = createCaptureId("operation");
                const acceptedOperationId = await ContributionCapture.recordOperation({
                    ...this.contributionInputForEntry(child, transactionId),
                    operationId: childOperationId,
                });
                if (!acceptedOperationId) continue;
                childOperationIds.push(childOperationId);
                this.contributionOperationIds.set(child as object, childOperationId);
            }
            const operationId = this.contributionOperationIds.get(entry as object);
            if (!operationId) return;
            await ContributionCapture.recordOperation({
                operationId,
                operationKind: "transaction.batch",
                transactionId,
                targets: this.contributionTargets(entry),
                childOperationIds,
                details: {
                    capturedFrom: "undo_manager",
                    operationCount: entry.entries.length,
                },
                origin: "human",
            });
        } catch {
            // Contribution capture is fail-open and must never change editor behavior.
        }
    }

    private static handleClipAdded = (event: any) => {
        if (this.applying) return;
        const clip = event?.detail?.clip;
        if (!clip) return;
        const snap = this.snapshotClip(clip);
        this.clipShadow.set(snap.id, snap);
        if (snap.type === "script") {
            this.scriptShadow.set(snap.id, {
                scriptCuts: snap.scriptCuts ?? [],
                gaps: snap.gaps ?? [],
                gapInsertions: snap.gapInsertions ?? [],
                splits: snap.splits ?? [],
                speakerOverrides: snap.speakerOverrides ?? [],
                speakerMuteRanges: snap.speakerMuteRanges ?? [],
                transcriptHiddenRanges: snap.transcriptHiddenRanges ?? [],
                wordCorrections: snap.wordCorrections ?? [],
                transcriptParagraphBreaks: snap.transcriptParagraphBreaks ?? [],
                transcriptMarkers: snap.transcriptMarkers ?? [],
            });
        }
        this.record({ kind: "clip:add", clip: snap, at: Date.now() });
    };

    private static handleClipRemoved = (event: any) => {
        if (this.applying) return;
        const clipId = String(event?.detail?.clipId ?? "");
        if (!clipId) return;
        const prev = this.clipShadow.get(clipId);
        if (!prev) return;
        this.clipShadow.delete(clipId);
        this.scriptShadow.delete(clipId);
        this.record({ kind: "clip:remove", clip: prev, at: Date.now() });
    };

    private static handleClipUpdated = (event: any) => {
        if (this.applying) return;
        if (event?.detail?.transient) return;
        const clip = event?.detail?.clip;
        if (!clip?.id) return;
        const next = this.snapshotClip(clip);
        const prev = this.clipShadow.get(next.id);
        this.clipShadow.set(next.id, next);
        if (!prev) return;
        if (shallowEqualJson(prev, next)) return;
        this.record({ kind: "clip:update", clipId: next.id, before: prev, after: next, at: Date.now() });
    };

    private static handleScriptLayerUpdated = (event: any) => {
        if (this.applying) return;
        const clipId = String(event?.detail?.script?.clipId ?? "");
        if (!clipId) return;
        const clip: any = CompositionService.getClipById(clipId);
        if (!clip || String(clip.type) !== "script") return;

        const next = {
            scriptCuts: cloneSnapshotArray(clip.scriptCuts),
            gaps: cloneSnapshotArray(clip.gaps),
            gapInsertions: cloneSnapshotArray(clip.gapInsertions),
            splits: cloneSnapshotArray(clip.splits),
            speakerOverrides: cloneSnapshotArray(clip.speakerOverrides),
            speakerMuteRanges: cloneSnapshotArray((clip as any).speakerMuteRanges),
            transcriptHiddenRanges: cloneSnapshotArray((clip as any).transcriptHiddenRanges),
            wordCorrections: cloneSnapshotArray((clip as any).wordCorrections),
            transcriptParagraphBreaks: cloneSnapshotArray((clip as any).transcriptParagraphBreaks),
            transcriptMarkers: cloneSnapshotArray((clip as any).transcriptMarkers),
        };
        const prev = this.scriptShadow.get(clipId);
        this.scriptShadow.set(clipId, next);
        // Keep the clip shadow in sync too.
        try {
            const cs = this.clipShadow.get(clipId);
            if (cs) {
                this.clipShadow.set(clipId, { ...cs, ...next });
            }
        } catch {}

        if (!prev) return;
        if (shallowEqualJson(prev, next)) return;
        this.record({ kind: "script:edits", clipId, before: prev, after: next, at: Date.now() });
    };

    private static handleCanvasResized = (event: any) => {
        if (this.applying) return;
        const w = Number(event?.detail?.width ?? 0);
        const h = Number(event?.detail?.height ?? 0);
        if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return;
        const prev = { width: this.canvasShadow.width, height: this.canvasShadow.height };
        const next = { width: w, height: h };
        if (prev.width === next.width && prev.height === next.height) return;
        this.canvasShadow.width = w;
        this.canvasShadow.height = h;
        this.record({ kind: "canvas:resize", before: prev, after: next, at: Date.now() });
    };

    private static handleCanvasBackgroundChanged = (event: any) => {
        if (this.applying) return;
        const color = String(event?.detail?.color ?? "");
        if (!color) return;
        const prev = this.canvasShadow.background;
        if (prev === color) return;
        this.canvasShadow.background = color;
        this.record({ kind: "canvas:bg", before: prev, after: color, at: Date.now() });
    };

    private static snapshotClip(clip: any): ClipSnapshot {
        const id = String(clip?.id ?? "");
        const type = String(clip?.type ?? "unknown");
        const layerId = String(clip?.track?.id ?? "");

        const snap: ClipSnapshot = {
            id,
            type,
            layerId: layerId || undefined,
            name: String(clip?.name ?? ""),
        };

        // Resolve asset mapping from store (best-effort).
        try {
            snap.assetId = CompositionStateStore.clipIdToAssetId[id] ?? null;
        } catch {
            snap.assetId = null;
        }
        try {
            snap.thumbnailId = CompositionStateStore.clipIdToThumbnailId[id] ?? null;
        } catch {
            snap.thumbnailId = null;
        }

        // Timing (MediaClip-style).
        try {
            if ("range" in clip && "offset" in clip) {
                snap.inSec = Number(clip.range?.[0]?.seconds ?? 0);
                snap.outSec = Number(clip.range?.[1]?.seconds ?? snap.inSec ?? 0);
                snap.offsetSec = Number(clip.offset?.seconds ?? 0);
            }
        } catch {}

        // Common props.
        snap.props = {
            gain: Number.isFinite(clip?.gain) ? Number(clip.gain) : 1,
            playbackRate: Number.isFinite(clip?.playbackRate) ? Number(clip.playbackRate) : 1,
            alpha: Number.isFinite(clip?.alpha) ? Number(clip.alpha) : undefined,
        };

        // Visual props.
        try {
            const pos = clip?.position;
            const x = typeof pos?.x === "number" ? pos.x : typeof clip?.x === "number" ? clip.x : undefined;
            const y = typeof pos?.y === "number" ? pos.y : typeof clip?.y === "number" ? clip.y : undefined;
            const scale = clip?.scale;
            const scaleX = typeof scale?.x === "number" ? scale.x : undefined;
            const scaleY = typeof scale?.y === "number" ? scale.y : undefined;

            const visual: ClipSnapshot["visual"] = {
                x,
                y,
                width: typeof clip?.width === "number" ? clip.width : undefined,
                height: typeof clip?.height === "number" ? clip.height : undefined,
                rotation: typeof clip?.rotation === "number" ? clip.rotation : undefined,
                alpha: typeof clip?.alpha === "number" ? clip.alpha : undefined,
                scaleX,
                scaleY,
            };

            if (typeof clip?.getCrop === "function") {
                const c = clip.getCrop();
                if (c) {
                    visual.crop = {
                        x: Number(c.x ?? 0),
                        y: Number(c.y ?? 0),
                        width: Number(c.width ?? 1),
                        height: Number(c.height ?? 1),
                    };
                }
            }

            // Only attach if there is anything meaningful.
            if (!shallowEqualJson(visual, {})) snap.visual = visual;
        } catch {}

        // Scene-keyed styling lives on otherwise ordinary captions/text/visual
        // clips. Script scene insertion remaps these keys, so they must be part
        // of the same undo checkpoint as the ScriptClip timing change.
        try {
            if (type === "captions" || type === "text") {
                snap.sceneStyleOverrides = cloneSnapshotValue(
                    (clip as any)._sceneStyleOverrides ?? {},
                );
            }
            if ((clip as any).sceneVisualOverrides && typeof (clip as any).sceneVisualOverrides === "object") {
                snap.sceneVisualOverrides = cloneSnapshotValue(
                    (clip as any).sceneVisualOverrides,
                );
            }
            if (
                (clip as any).sceneVisualOverridesByAspect &&
                typeof (clip as any).sceneVisualOverridesByAspect === "object"
            ) {
                snap.sceneVisualOverridesByAspect = cloneSnapshotValue(
                    (clip as any).sceneVisualOverridesByAspect,
                );
            }
        } catch {}

        // Script-specific fields.
        if (type === "script" && clip instanceof ScriptLayerClip) {
            try {
                snap.scriptCuts = cloneSnapshotArray((clip as any).scriptCuts);
                snap.gaps = cloneSnapshotArray((clip as any).gaps);
                snap.gapInsertions = cloneSnapshotArray((clip as any).gapInsertions);
                snap.splits = cloneSnapshotArray((clip as any).splits);
                snap.speakerOverrides = cloneSnapshotArray((clip as any).speakerOverrides);
                snap.speakerMuteRanges = cloneSnapshotArray((clip as any).speakerMuteRanges);
                snap.transcriptHiddenRanges = cloneSnapshotArray((clip as any).transcriptHiddenRanges);
                snap.wordCorrections = cloneSnapshotArray((clip as any).wordCorrections);
                snap.transcriptParagraphBreaks = cloneSnapshotArray((clip as any).transcriptParagraphBreaks);
                snap.transcriptMarkers = cloneSnapshotArray((clip as any).transcriptMarkers);
            } catch {}
            try {
                const setup = (clip as any).multicamCameraSetup;
                snap.multicamCameraSetup = setup ? JSON.parse(JSON.stringify(setup)) : null;
            } catch {}
            try {
                const map: Map<number, any> | undefined = (clip as any)?.sceneProperties;
                if (map && typeof (map as any).entries === "function") {
                    const out: any[] = [];
                    for (const [sceneIndexRaw, propsRaw] of (map as any).entries()) {
                        const sceneIndex = Number(sceneIndexRaw);
                        if (!Number.isFinite(sceneIndex) || sceneIndex < 0) continue;

                        const props = propsRaw ?? {};
                        const rec: any = { sceneIndex };

                        if (Number.isFinite((props as any)?.gain)) rec.gain = Number((props as any).gain);
                        if (Number.isFinite((props as any)?.playbackRate)) rec.playbackRate = Number((props as any).playbackRate);
                        if (typeof (props as any)?.backgroundColor === "string" && String((props as any).backgroundColor).trim()) {
                            rec.backgroundColor = String((props as any).backgroundColor).trim();
                        }

                        // Per-source audio gain multipliers
                        try {
                            const audioSourceGains = (props as any)?.audioSourceGains;
                            if (audioSourceGains && typeof audioSourceGains?.entries === "function") {
                                const arr: any[] = [];
                                for (const [sourceIndexRaw, gainRaw] of audioSourceGains.entries()) {
                                    const sourceIndex = Number(sourceIndexRaw);
                                    const gain = Number(gainRaw);
                                    if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                                    if (!Number.isFinite(gain)) continue;
                                    arr.push({ sourceIndex, gain });
                                }
                                arr.sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0));
                                if (arr.length > 0) rec.audioSourceGains = arr;
                            }
                        } catch {}

                        if (typeof (props as any)?.layout === "string") rec.layout = String((props as any).layout);
                        try {
                            const byAspect = (props as any)?.layoutByAspect;
                            if (byAspect && typeof byAspect === "object") {
                                const cleaned: any = {};
                                for (const key of ["horizontal", "vertical", "square"]) {
                                    if (!Object.prototype.hasOwnProperty.call(byAspect, key)) continue;
                                    const v = (byAspect as any)[key];
                                    if (v === null) cleaned[key] = null;
                                    else if (typeof v === "string" && v) cleaned[key] = v;
                                }
                                if (Object.keys(cleaned).length > 0) rec.layoutByAspect = cleaned;
                            }
                        } catch {}

                        try {
                            if ((props as any)?.inTransition) rec.inTransition = JSON.parse(JSON.stringify((props as any).inTransition));
                            if ((props as any)?.outTransition) rec.outTransition = JSON.parse(JSON.stringify((props as any).outTransition));
                        } catch {}

                        // visualOverrides (legacy)
                        try {
                            const visualOverrides = (props as any)?.visualOverrides;
                            if (visualOverrides && typeof visualOverrides?.entries === "function") {
                                const vis: any[] = [];
                                for (const [sourceIndexRaw, vRaw] of visualOverrides.entries()) {
                                    const sourceIndex = Number(sourceIndexRaw);
                                    if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                                    const v = vRaw ?? {};
                                    const vrec: any = { sourceIndex };
                                    if (Number.isFinite(v.alpha)) vrec.alpha = Number(v.alpha);
                                    if (Number.isFinite(v.x)) vrec.x = Number(v.x);
                                    if (Number.isFinite(v.y)) vrec.y = Number(v.y);
                                    if (Number.isFinite(v.width)) vrec.width = Number(v.width);
                                    if (Number.isFinite(v.height)) vrec.height = Number(v.height);
                                    try {
                                        const crop = (v as any)?.crop;
                                        if (crop && typeof crop === "object") {
                                            const x = Number((crop as any).x);
                                            const y = Number((crop as any).y);
                                            const width = Number((crop as any).width);
                                            const height = Number((crop as any).height);
                                            if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                                                vrec.crop = { x, y, width, height };
                                            }
                                        }
                                    } catch {}
                                    if (typeof (v as any)?.maskShape === "string") vrec.maskShape = String((v as any).maskShape);
                                    if (typeof (v as any)?.rotation === "number" && Number.isFinite((v as any).rotation)) {
                                        vrec.rotation = Number((v as any).rotation);
                                    }
                                    if (Object.keys(vrec).length > 1) vis.push(vrec);
                                }
                                vis.sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0));
                                if (vis.length > 0) rec.visualOverrides = vis;
                            }
                        } catch {}

                        // visualOverridesByAspect
                        try {
                            const byAspect = (props as any)?.visualOverridesByAspect;
                            if (byAspect && typeof byAspect === "object") {
                                const outByAspect: any = {};
                                for (const [presetKey, mapRaw] of Object.entries(byAspect as any)) {
                                    const m: any = mapRaw as any;
                                    if (!m || typeof m?.entries !== "function") continue;
                                    const arr: any[] = [];
                                    for (const [sourceIndexRaw, vRaw] of m.entries()) {
                                        const sourceIndex = Number(sourceIndexRaw);
                                        if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                                        const v = vRaw ?? {};
                                        const vrec: any = { sourceIndex };
                                        if (Number.isFinite(v.alpha)) vrec.alpha = Number(v.alpha);
                                        if (Number.isFinite(v.x)) vrec.x = Number(v.x);
                                        if (Number.isFinite(v.y)) vrec.y = Number(v.y);
                                        if (Number.isFinite(v.width)) vrec.width = Number(v.width);
                                        if (Number.isFinite(v.height)) vrec.height = Number(v.height);
                                        try {
                                            const crop = (v as any)?.crop;
                                            if (crop && typeof crop === "object") {
                                                const x = Number((crop as any).x);
                                                const y = Number((crop as any).y);
                                                const width = Number((crop as any).width);
                                                const height = Number((crop as any).height);
                                                if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                                                    vrec.crop = { x, y, width, height };
                                                }
                                            }
                                        } catch {}
                                        if (typeof (v as any)?.maskShape === "string") vrec.maskShape = String((v as any).maskShape);
                                        if (typeof (v as any)?.rotation === "number" && Number.isFinite((v as any).rotation)) {
                                            vrec.rotation = Number((v as any).rotation);
                                        }
                                        if (Object.keys(vrec).length > 1) arr.push(vrec);
                                    }
                                    arr.sort((a, b) => (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0));
                                    if (arr.length > 0) outByAspect[String(presetKey)] = arr;
                                }
                                if (Object.keys(outByAspect).length > 0) rec.visualOverridesByAspect = outByAspect;
                            }
                        } catch {}

                        if (Object.keys(rec).length > 1) out.push(rec);
                    }

                    out.sort((a, b) => (a.sceneIndex ?? 0) - (b.sceneIndex ?? 0));
                    snap.sceneProperties = out;
                }
            } catch {}
            try {
                const cfgs = clip.getInputConfigs();
                const videoInputs = new Set(
                    (typeof (clip as any).getVideoEntries === "function"
                        ? (clip as any).getVideoEntries()
                        : []
                    ).map((entry: any) => entry?.input),
                );
                snap.sources = cfgs.map((c, idx) => ({
                    assetId: String(c.assetId ?? ""),
                    kind: (videoInputs.has(c.input) ? "video" : "audio") as "video" | "audio",
                    name: c.name ?? "",
                    color: c.color ?? undefined,
                    gain: Number.isFinite(c.gain) ? Number(c.gain) : 1.0,
                    playbackRate: Number.isFinite(c.playbackRate) ? Number(c.playbackRate) : 1.0,
                    offsetSec: Number.isFinite(c.offsetSec) ? Number(c.offsetSec) : 0,
                    range: c.range ? { inSec: Number(c.range.inSec ?? 0), outSec: Number(c.range.outSec ?? 0) } : undefined,
                    segments:
                        Array.isArray((c as any).segments) && (c as any).segments.length > 0
                            ? ((c as any).segments as any[])
                                  .map((s: any) => {
                                      const offsetSec = Number(s?.offsetSec ?? NaN);
                                      const rin = Number(s?.range?.inSec ?? NaN);
                                      const rout = Number(s?.range?.outSec ?? NaN);
                                      if (!Number.isFinite(offsetSec) || !Number.isFinite(rin) || !Number.isFinite(rout) || rout <= rin) return null;
                                      const rec: any = {
                                          id: s?.id ? String(s.id) : undefined,
                                          offsetSec,
                                          range: { inSec: rin, outSec: rout },
                                      };
                                      const pr = s?.playbackRate;
                                      if (Number.isFinite(pr) && Number(pr) > 0) rec.playbackRate = Number(pr);
                                      return rec;
                                  })
                                  .filter(Boolean)
                            : undefined,
                    visual: c.visual
                        ? {
                              x: Number(c.visual.x ?? 0),
                              y: Number(c.visual.y ?? 0),
                              width: Number(c.visual.width ?? 0),
                              height: Number(c.visual.height ?? 0),
                              alpha: Number(c.visual.alpha ?? 1),
                              crop: c.visual.crop
                                  ? {
                                        x: Number(c.visual.crop.x ?? 0),
                                        y: Number(c.visual.crop.y ?? 0),
                                        width: Number(c.visual.crop.width ?? 1),
                                        height: Number(c.visual.crop.height ?? 1),
                                    }
                                  : undefined,
                          }
                        : undefined,
                    layerIndex: Number.isFinite(c.layerIndex) ? Number(c.layerIndex) : idx,
                })).filter(s => s.assetId);
            } catch {}
        }

        return snap;
    }

    private static async applyInverse(entry: UndoEntry): Promise<void> {
        switch (entry.kind) {
            case "batch":
                for (let i = entry.entries.length - 1; i >= 0; i -= 1) {
                    await this.applyInverse(entry.entries[i]);
                }
                return;
            case "clip:update":
                await this.applySnapshotToExistingClip(entry.clipId, entry.before);
                return;
            case "clip:add":
                CompositionService.deleteClipById(entry.clip.id);
                CompositionStateStore.removeMetadataForClip(entry.clip.id);
                return;
            case "clip:remove":
                await this.recreateClip(entry.clip);
                return;
            case "script:edits":
                await this.applyScriptEdits(entry.clipId, entry.before);
                return;
            case "canvas:resize":
                CompositionService.composition.resize(entry.before.width, entry.before.height);
                return;
            case "canvas:bg":
                CompositionService.composition.changeBackground(entry.before as any);
                return;
        }
    }

    private static async applyForward(entry: UndoEntry): Promise<void> {
        switch (entry.kind) {
            case "batch":
                for (const child of entry.entries) {
                    await this.applyForward(child);
                }
                return;
            case "clip:update":
                await this.applySnapshotToExistingClip(entry.clipId, entry.after);
                return;
            case "clip:add":
                await this.recreateClip(entry.clip);
                return;
            case "clip:remove":
                CompositionService.deleteClipById(entry.clip.id);
                CompositionStateStore.removeMetadataForClip(entry.clip.id);
                return;
            case "script:edits":
                await this.applyScriptEdits(entry.clipId, entry.after);
                return;
            case "canvas:resize":
                CompositionService.composition.resize(entry.after.width, entry.after.height);
                return;
            case "canvas:bg":
                CompositionService.composition.changeBackground(entry.after as any);
                return;
        }
    }

    private static async applySnapshotToExistingClip(clipId: string, snap: ClipSnapshot): Promise<void> {
        const clip: any = CompositionService.getClipById(clipId);
        if (!clip) return;

        // Timing
        try {
            if ("range" in clip && "offset" in clip) {
                const inSec = Number(snap.inSec ?? 0);
                const outSec = Number(snap.outSec ?? inSec);
                const offsetSec = Number(snap.offsetSec ?? 0);
                clip.range = [Timestamp.fromSeconds(inSec), Timestamp.fromSeconds(outSec)];
                clip.offset = Timestamp.fromSeconds(offsetSec);
            }
        } catch {}

        // Props
        try {
            const pr = Number(snap.props?.playbackRate ?? 1);
            if (typeof clip.playbackRate === "number" || clip.playbackRate === undefined) {
                clip.playbackRate = Number.isFinite(pr) ? pr : 1;
            }
        } catch {}
        try {
            const g = Number(snap.props?.gain ?? 1);
            if (typeof clip.gain === "number" || clip.gain === undefined) {
                clip.gain = Number.isFinite(g) ? g : 1;
            }
        } catch {}
        try {
            const a = Number(snap.props?.alpha ?? 1);
            if ("alpha" in clip) clip.alpha = Number.isFinite(a) ? a : 1;
        } catch {}

        // Visual
        const v = snap.visual;
        if (v) {
            try {
                if (typeof (clip as any).setCrop === "function" && v.crop) {
                    (clip as any).setCrop(v.crop);
                }
            } catch {}
            try {
                if (typeof v.x === "number" && typeof v.y === "number") {
                    if ("position" in clip) clip.position = { x: v.x, y: v.y };
                    else {
                        if ("x" in clip) clip.x = v.x;
                        if ("y" in clip) clip.y = v.y;
                    }
                }
            } catch {}
            try { if (typeof v.width === "number") clip.width = v.width; } catch {}
            try { if (typeof v.height === "number") clip.height = v.height; } catch {}
            try { if (typeof v.rotation === "number" && "rotation" in clip) clip.rotation = v.rotation; } catch {}
            try { if (typeof v.scaleX === "number" && typeof v.scaleY === "number" && "scale" in clip) clip.scale = { x: v.scaleX, y: v.scaleY }; } catch {}
        }

        try {
            if (snap.sceneStyleOverrides !== undefined) {
                (clip as any)._sceneStyleOverrides = cloneSnapshotValue(snap.sceneStyleOverrides);
            }
            if (snap.sceneVisualOverrides !== undefined) {
                (clip as any).sceneVisualOverrides = cloneSnapshotValue(snap.sceneVisualOverrides);
            }
            if (snap.sceneVisualOverridesByAspect !== undefined) {
                (clip as any).sceneVisualOverridesByAspect = cloneSnapshotValue(
                    snap.sceneVisualOverridesByAspect,
                );
            }
        } catch {}

        // Script edits
        if (snap.type === "script" && clip instanceof ScriptLayerClip) {
            try {
                (clip as any).applyPersistedEdits?.({
                    scriptCuts: Array.isArray(snap.scriptCuts) ? snap.scriptCuts : [],
                    gaps: Array.isArray(snap.gaps) ? snap.gaps : [],
                    gapInsertions: Array.isArray(snap.gapInsertions) ? snap.gapInsertions : [],
                    splits: Array.isArray(snap.splits) ? snap.splits : [],
                    speakerOverrides: Array.isArray(snap.speakerOverrides) ? snap.speakerOverrides : [],
                    speakerMuteRanges: Array.isArray(snap.speakerMuteRanges) ? snap.speakerMuteRanges : [],
                    transcriptHiddenRanges: Array.isArray(snap.transcriptHiddenRanges) ? snap.transcriptHiddenRanges : [],
                    wordCorrections: Array.isArray(snap.wordCorrections) ? snap.wordCorrections : [],
                    transcriptParagraphBreaks: Array.isArray(snap.transcriptParagraphBreaks) ? snap.transcriptParagraphBreaks : [],
                    transcriptMarkers: Array.isArray(snap.transcriptMarkers) ? snap.transcriptMarkers : [],
                });
            } catch {}

            // Restore per-source segment placement + config-level values (gain/playback/visual).
            // This is critical for undo/redo of operations like inserting empty scenes or copy/paste.
            try {
                const next = Array.isArray(snap.sources) ? snap.sources : [];
                const targetAssetIds = new Set(
                    next
                        .map((source: any) => String(source?.assetId ?? "").trim())
                        .filter(Boolean),
                );

                let cfgs: any[] = typeof (clip as any).getInputConfigs === "function" ? (clip as any).getInputConfigs() : [];
                if (Array.isArray(snap.sources)) {
                    // Newly appended sources are always a suffix. Remove them in reverse so
                    // config/runtime indices for the retained sources never shift.
                    for (let index = cfgs.length - 1; index >= 0; index -= 1) {
                        const assetId = String(cfgs[index]?.assetId ?? "").trim();
                        if (assetId && !targetAssetIds.has(assetId)) {
                            const removed = (clip as any).removeLastInputConfigByAssetId?.(assetId);
                            if (!removed) {
                                console.warn("[UndoManager] Unable to remove appended ScriptLayer source", assetId);
                                break;
                            }
                        }
                    }

                    cfgs = typeof (clip as any).getInputConfigs === "function" ? (clip as any).getInputConfigs() : [];

                    // Redo may need to recreate sources removed by undo.
                    for (const src of next) {
                        const assetId = String((src as any)?.assetId ?? "").trim();
                        if (!assetId || cfgs.some((c: any) => String(c?.assetId ?? "") === assetId)) continue;
                        if (!this.resolveAssetUrl) {
                            console.warn("[UndoManager] Cannot restore ScriptLayer source: resolveAssetUrl not set");
                            continue;
                        }

                        const preferredKind = (src as any)?.kind === "video" ? "video" : "audio";
                        let url = await this.resolveAssetUrl(assetId, preferredKind);
                        if (!url) {
                            const fallbackKind = preferredKind === "video" ? "audio" : "video";
                            url = await this.resolveAssetUrl(assetId, fallbackKind);
                        }
                        if (!url) continue;

                        const restoredConfig: ScriptLayerInputConfig = {
                            input: new Input({ source: new UrlSource(url), formats: ALL_FORMATS }),
                            assetId,
                            name: (src as any).name ?? "",
                            color: (src as any).color ?? undefined,
                            gain: Number.isFinite((src as any).gain) ? Number((src as any).gain) : 1,
                            playbackRate: Number.isFinite((src as any).playbackRate)
                                ? Number((src as any).playbackRate)
                                : 1,
                            offsetSec: Number.isFinite((src as any).offsetSec)
                                ? Number((src as any).offsetSec)
                                : 0,
                            range: (src as any).range
                                ? {
                                      inSec: Number((src as any).range.inSec ?? 0),
                                      outSec: Number((src as any).range.outSec ?? 0),
                                  }
                                : undefined,
                            segments: Array.isArray((src as any).segments)
                                ? ((src as any).segments as any[]).map((segment: any) => ({
                                      id: segment?.id,
                                      offsetSec: Number(segment?.offsetSec ?? 0),
                                      playbackRate: segment?.playbackRate,
                                      range: {
                                          inSec: Number(segment?.range?.inSec ?? 0),
                                          outSec: Number(segment?.range?.outSec ?? 0),
                                      },
                                  }))
                                : undefined,
                            visual: (src as any).visual ?? undefined,
                            layerIndex: Number.isFinite((src as any).layerIndex)
                                ? Number((src as any).layerIndex)
                                : undefined,
                        };
                        await (clip as any).appendInputConfig?.(restoredConfig);
                        cfgs = typeof (clip as any).getInputConfigs === "function" ? (clip as any).getInputConfigs() : [];
                    }
                }

                for (const src of next) {
                    const assetId = String((src as any)?.assetId ?? "").trim();
                    if (!assetId) continue;
                    const cfg = cfgs.find((c: any) => String(c?.assetId ?? "") === assetId);
                    if (!cfg) continue;

                    cfg.name = (src as any).name ?? cfg.name;
                    cfg.color = (src as any).color ?? cfg.color;
                    cfg.gain = Number.isFinite((src as any).gain) ? Number((src as any).gain) : cfg.gain;
                    cfg.playbackRate = Number.isFinite((src as any).playbackRate) ? Number((src as any).playbackRate) : cfg.playbackRate;
                    cfg.offsetSec = Number.isFinite((src as any).offsetSec) ? Number((src as any).offsetSec) : cfg.offsetSec;
                    cfg.range = (src as any).range
                        ? { inSec: Number((src as any).range.inSec ?? 0), outSec: Number((src as any).range.outSec ?? 0) }
                        : cfg.range;
                    cfg.visual = (src as any).visual ?? cfg.visual;
                    cfg.layerIndex = Number.isFinite((src as any).layerIndex) ? Number((src as any).layerIndex) : cfg.layerIndex;

                    const segsRaw: any[] = Array.isArray((src as any).segments) ? ((src as any).segments as any[]) : [];
                    if (segsRaw.length > 0) {
                        cfg.segments = segsRaw
                            .map((s: any) => {
                                const segId = s?.id ? String(s.id) : crypto?.randomUUID?.() ?? `seg_${Date.now()}_${Math.random()}`;
                                const offsetSec = Number(s?.offsetSec ?? NaN);
                                const rin = Number(s?.range?.inSec ?? NaN);
                                const rout = Number(s?.range?.outSec ?? NaN);
                                if (!Number.isFinite(offsetSec) || !Number.isFinite(rin) || !Number.isFinite(rout) || rout <= rin) return null;
                                const rec: any = { id: segId, offsetSec, range: { inSec: rin, outSec: rout } };
                                const pr = s?.playbackRate;
                                if (Number.isFinite(pr) && Number(pr) > 0) rec.playbackRate = Number(pr);
                                return rec;
                            })
                            .filter(Boolean)
                            .sort((a: any, b: any) => Number(a.offsetSec ?? 0) - Number(b.offsetSec ?? 0));
                    } else {
                        // No segments stored in this snapshot; clear to fall back to legacy range/offset.
                        if (Array.isArray(cfg.segments)) cfg.segments = [];
                        else delete cfg.segments;
                    }

                    // Keep derived runtime entries in sync (segments are referenced by pointer in many places).
                    try {
                        for (const e of (clip as any).audioEntries ?? []) {
                            if (e?.input === cfg.input) (e as any).segments = cfg.segments;
                        }
                        for (const e of (clip as any).videoEntries ?? []) {
                            if (e?.input === cfg.input) (e as any).segments = cfg.segments;
                        }
                    } catch {}
                }

                // appendInputConfig can only extend duration; restore the snapshot's exact
                // spine bounds after source reconciliation (important for undoing inserts).
                const snapOutSec = Number(snap.outSec ?? NaN);
                if (Number.isFinite(snapOutSec)) {
                    try { (clip as any).duration.seconds = snapOutSec; } catch {}
                    try {
                        const snapInSec = Number(snap.inSec ?? 0);
                        (clip as any).range = [
                            Timestamp.fromSeconds(snapInSec),
                            Timestamp.fromSeconds(snapOutSec),
                        ];
                    } catch {}
                }
            } catch {}

            // Restore script clip extra state: multicam setup + sceneProperties
            try {
                (clip as any).multicamCameraSetup = snap.multicamCameraSetup ? JSON.parse(JSON.stringify(snap.multicamCameraSetup)) : null;
            } catch {}

            try {
                const sceneProps = Array.isArray(snap.sceneProperties) ? snap.sceneProperties : [];
                const nextMap = new Map<number, any>();
                for (const sp of sceneProps) {
                    const sceneIndex = Number((sp as any)?.sceneIndex);
                    if (!Number.isFinite(sceneIndex) || sceneIndex < 0) continue;
                    const props: any = {};
                    if (Number.isFinite((sp as any)?.gain)) props.gain = Number((sp as any).gain);
                    if (Number.isFinite((sp as any)?.playbackRate)) props.playbackRate = Number((sp as any).playbackRate);
                    if (typeof (sp as any)?.backgroundColor === "string" && String((sp as any).backgroundColor).trim()) {
                        props.backgroundColor = String((sp as any).backgroundColor).trim();
                    }
                    try {
                        const ag = (sp as any)?.audioSourceGains;
                        if (Array.isArray(ag) && ag.length > 0) {
                            const m = new Map<number, number>();
                            for (const entry of ag) {
                                const sourceIndex = Number((entry as any)?.sourceIndex);
                                const gain = Number((entry as any)?.gain);
                                if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                                if (!Number.isFinite(gain)) continue;
                                m.set(sourceIndex, gain);
                            }
                            if (m.size > 0) props.audioSourceGains = m;
                        }
                    } catch {}
                    if (typeof (sp as any)?.layout === "string") props.layout = String((sp as any).layout);
                    if ((sp as any)?.layoutByAspect && typeof (sp as any).layoutByAspect === "object") {
                        const cleaned: any = {};
                        for (const key of ["horizontal", "vertical", "square"]) {
                            if (!Object.prototype.hasOwnProperty.call((sp as any).layoutByAspect, key)) continue;
                            const v = ((sp as any).layoutByAspect as any)[key];
                            if (v === null) cleaned[key] = null;
                            else if (typeof v === "string" && v) cleaned[key] = v;
                        }
                        if (Object.keys(cleaned).length > 0) props.layoutByAspect = cleaned;
                    }
                    if ((sp as any)?.inTransition) props.inTransition = JSON.parse(JSON.stringify((sp as any).inTransition));
                    if ((sp as any)?.outTransition) props.outTransition = JSON.parse(JSON.stringify((sp as any).outTransition));

                    const vo = (sp as any)?.visualOverrides;
                    if (Array.isArray(vo) && vo.length > 0) {
                        const m = new Map<number, any>();
                        for (const entry of vo) {
                            const sourceIndex = Number((entry as any)?.sourceIndex);
                            if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                            const v: any = {};
                            if (Number.isFinite((entry as any)?.alpha)) v.alpha = Number((entry as any).alpha);
                            if (Number.isFinite((entry as any)?.x)) v.x = Number((entry as any).x);
                            if (Number.isFinite((entry as any)?.y)) v.y = Number((entry as any).y);
                            if (Number.isFinite((entry as any)?.width)) v.width = Number((entry as any).width);
                            if (Number.isFinite((entry as any)?.height)) v.height = Number((entry as any).height);
                            try {
                                const crop = (entry as any)?.crop;
                                if (crop && typeof crop === "object") {
                                    const x = Number((crop as any).x);
                                    const y = Number((crop as any).y);
                                    const width = Number((crop as any).width);
                                    const height = Number((crop as any).height);
                                    if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                                        v.crop = { x, y, width, height };
                                    }
                                }
                            } catch {}
                            if (typeof (entry as any)?.maskShape === "string") v.maskShape = String((entry as any).maskShape);
                            if (Number.isFinite((entry as any)?.rotation)) v.rotation = Number((entry as any).rotation);
                            if (Object.keys(v).length > 0) m.set(sourceIndex, v);
                        }
                        if (m.size > 0) props.visualOverrides = m;
                    }

                    const voba = (sp as any)?.visualOverridesByAspect;
                    if (voba && typeof voba === "object") {
                        const outByAspect: any = {};
                        for (const [presetKey, arrRaw] of Object.entries(voba)) {
                            const arr: any[] = Array.isArray(arrRaw) ? (arrRaw as any[]) : [];
                            if (!arr.length) continue;
                            const m = new Map<number, any>();
                            for (const entry of arr) {
                                const sourceIndex = Number((entry as any)?.sourceIndex);
                                if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                                const v: any = {};
                                if (Number.isFinite((entry as any)?.alpha)) v.alpha = Number((entry as any).alpha);
                                if (Number.isFinite((entry as any)?.x)) v.x = Number((entry as any).x);
                                if (Number.isFinite((entry as any)?.y)) v.y = Number((entry as any).y);
                                if (Number.isFinite((entry as any)?.width)) v.width = Number((entry as any).width);
                                if (Number.isFinite((entry as any)?.height)) v.height = Number((entry as any).height);
                                try {
                                    const crop = (entry as any)?.crop;
                                    if (crop && typeof crop === "object") {
                                        const x = Number((crop as any).x);
                                        const y = Number((crop as any).y);
                                        const width = Number((crop as any).width);
                                        const height = Number((crop as any).height);
                                        if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                                            v.crop = { x, y, width, height };
                                        }
                                    }
                                } catch {}
                                if (typeof (entry as any)?.maskShape === "string") v.maskShape = String((entry as any).maskShape);
                                if (Number.isFinite((entry as any)?.rotation)) v.rotation = Number((entry as any).rotation);
                                if (Object.keys(v).length > 0) m.set(sourceIndex, v);
                            }
                            if (m.size > 0) outByAspect[String(presetKey)] = m;
                        }
                        if (Object.keys(outByAspect).length > 0) props.visualOverridesByAspect = outByAspect;
                    }

                    if (Object.keys(props).length > 0) nextMap.set(sceneIndex, props);
                }

                (clip as any).sceneProperties = nextMap;
                (clip as any)._scenesCache = null;
            } catch {}

            // Rebuild mixer scheduling and composition timing after source/scene restoration.
            try { await (CompositionService.composition as any)?.onClipTimingChanged?.(); } catch {}

            // Broadcast script-layer state so scene bar/transcript stay consistent.
            try {
                const hasVideo = (clip.getVideoSources?.()?.length ?? 0) > 0;
                CompositionService.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
                    clipId,
                    cuts: clip.scriptCuts ?? [],
                    gaps: clip.gaps ?? [],
                    gapInsertions: (clip as any).gapInsertions ?? [],
                    segments: clip.scriptSegments ?? [],
                    splits: clip.splits ?? [],
                    duration: clip.duration?.seconds ?? 0,
                    hasVideo,
                });
                (CompositionService.composition as any).trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                    script: {
                        layerId: CompositionService.composition.scriptLayer?.layerId ?? null,
                        clipId,
                        cutsChanged: true,
                        gapsChanged: true,
                        insertionsChanged: true,
                        splitsChanged: true,
                        speakerOverridesChanged: true,
                        speakerMutesChanged: true,
                        transcriptHiddenRangesChanged: true,
                        wordCorrectionsChanged: true,
                        transcriptParagraphBreaksChanged: true,
                        transcriptMarkersChanged: true,
                    },
                });
            } catch {}
        }

        try { clip.trigger?.("frame", undefined); } catch {}
        try { CompositionService.composition.computeFrame(); } catch {}
        // Notify store/persistence subscribers (visual changes don't emit on their own).
        try {
            (CompositionService.composition as any)?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip });
            (CompositionService.composition as any)?.trigger?.(EngineInternalEventsEnum.CLIP_UPDATED, { clip });
        } catch {}

        // Update shadow baseline.
        const next = this.snapshotClip(clip);
        this.clipShadow.set(next.id, next);
        if (next.type === "script") {
            this.scriptShadow.set(next.id, {
                scriptCuts: next.scriptCuts ?? [],
                gaps: next.gaps ?? [],
                gapInsertions: next.gapInsertions ?? [],
                splits: next.splits ?? [],
                speakerOverrides: next.speakerOverrides ?? [],
                speakerMuteRanges: next.speakerMuteRanges ?? [],
                transcriptHiddenRanges: next.transcriptHiddenRanges ?? [],
                wordCorrections: next.wordCorrections ?? [],
                transcriptParagraphBreaks: next.transcriptParagraphBreaks ?? [],
                transcriptMarkers: next.transcriptMarkers ?? [],
            });
        }
    }

    private static async applyScriptEdits(
        clipId: string,
        edits: Pick<ClipSnapshot, "scriptCuts" | "gaps" | "gapInsertions" | "splits" | "speakerOverrides" | "speakerMuteRanges" | "transcriptHiddenRanges" | "wordCorrections" | "transcriptParagraphBreaks" | "transcriptMarkers">
    ): Promise<void> {
        const clip: any = CompositionService.getClipById(clipId);
        if (!clip || String(clip.type) !== "script") return;
        try {
            clip.applyPersistedEdits?.({
                scriptCuts: Array.isArray(edits.scriptCuts) ? edits.scriptCuts : [],
                gaps: Array.isArray(edits.gaps) ? edits.gaps : [],
                gapInsertions: Array.isArray(edits.gapInsertions) ? edits.gapInsertions : [],
                splits: Array.isArray(edits.splits) ? edits.splits : [],
                speakerOverrides: Array.isArray(edits.speakerOverrides) ? edits.speakerOverrides : [],
                speakerMuteRanges: Array.isArray(edits.speakerMuteRanges) ? edits.speakerMuteRanges : [],
                transcriptHiddenRanges: Array.isArray(edits.transcriptHiddenRanges) ? edits.transcriptHiddenRanges : [],
                wordCorrections: Array.isArray(edits.wordCorrections) ? edits.wordCorrections : [],
                transcriptParagraphBreaks: Array.isArray(edits.transcriptParagraphBreaks) ? edits.transcriptParagraphBreaks : [],
                transcriptMarkers: Array.isArray(edits.transcriptMarkers) ? edits.transcriptMarkers : [],
            });
        } catch {}

        // Broadcast state so UI + persistence sync.
        try {
            const hasVideo = (clip.getVideoSources?.()?.length ?? 0) > 0;
            CompositionService.composition.trigger(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, {
                clipId,
                cuts: clip.scriptCuts ?? [],
                gaps: clip.gaps ?? [],
                gapInsertions: clip.gapInsertions ?? [],
                segments: clip.scriptSegments ?? [],
                splits: clip.splits ?? [],
                duration: clip.duration?.seconds ?? 0,
                hasVideo,
            });
            (CompositionService.composition as any).trigger(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
                script: {
                    layerId: CompositionService.composition.scriptLayer?.layerId ?? null,
                    clipId,
                    cutsChanged: true,
                    gapsChanged: true,
                    insertionsChanged: true,
                    speakerOverridesChanged: true,
                    speakerMutesChanged: true,
                    transcriptHiddenRangesChanged: true,
                    wordCorrectionsChanged: true,
                    transcriptParagraphBreaksChanged: true,
                    transcriptMarkersChanged: true,
                },
            });
        } catch {}

        this.scriptShadow.set(clipId, {
            scriptCuts: cloneSnapshotArray(clip.scriptCuts),
            gaps: cloneSnapshotArray(clip.gaps),
            gapInsertions: cloneSnapshotArray(clip.gapInsertions),
            splits: cloneSnapshotArray(clip.splits),
            speakerOverrides: cloneSnapshotArray(clip.speakerOverrides),
            speakerMuteRanges: cloneSnapshotArray((clip as any).speakerMuteRanges),
            transcriptHiddenRanges: cloneSnapshotArray((clip as any).transcriptHiddenRanges),
            wordCorrections: cloneSnapshotArray((clip as any).wordCorrections),
            transcriptParagraphBreaks: cloneSnapshotArray((clip as any).transcriptParagraphBreaks),
            transcriptMarkers: cloneSnapshotArray((clip as any).transcriptMarkers),
        });
    }

    private static async recreateClip(snap: ClipSnapshot): Promise<void> {
        if (!this.resolveAssetUrl) {
            console.warn("[UndoManager] Cannot recreate clip: resolveAssetUrl not set");
            return;
        }
        if (!CompositionService.isInitialized()) return;
        const comp: any = CompositionService.composition;

        // Avoid duplicates.
        if (snap?.id && CompositionService.getClipById(snap.id)) return;

        const layerId = snap.layerId || "";
        const type = String(snap.type ?? "").toLowerCase();

        let clip: any | null = null;

        if (type === "script") {
            const cfgs = Array.isArray(snap.sources) ? snap.sources : [];
            if (!cfgs.length) return;
            const inputConfigs: ScriptLayerInputConfig[] = [];
            for (const src of cfgs) {
                const assetId = String(src.assetId ?? "");
                if (!assetId) continue;
                const url = await this.resolveAssetUrl(assetId, "video");
                if (!url) continue;
                inputConfigs.push({
                    segments: Array.isArray((src as any).segments)
                        ? ((src as any).segments as any[])
                              .map((s: any) => {
                                  const segId = s?.id ? String(s.id) : crypto?.randomUUID?.() ?? `seg_${Date.now()}_${Math.random()}`;
                                  const offsetSec = Number(s?.offsetSec ?? NaN);
                                  const rin = Number(s?.range?.inSec ?? NaN);
                                  const rout = Number(s?.range?.outSec ?? NaN);
                                  if (!Number.isFinite(offsetSec) || !Number.isFinite(rin) || !Number.isFinite(rout) || rout <= rin) return null;
                                  const rec: any = { id: segId, offsetSec, range: { inSec: rin, outSec: rout } };
                                  const pr = s?.playbackRate;
                                  if (Number.isFinite(pr) && Number(pr) > 0) rec.playbackRate = Number(pr);
                                  return rec;
                              })
                              .filter(Boolean)
                        : undefined,
                    input: new Input({ source: new UrlSource(url), formats: ALL_FORMATS }),
                    assetId,
                    name: src.name ?? "",
                    color: src.color ?? undefined,
                    gain: Number.isFinite(src.gain) ? Number(src.gain) : 1.0,
                    playbackRate: Number.isFinite(src.playbackRate) ? Number(src.playbackRate) : 1.0,
                    offsetSec: Number.isFinite(src.offsetSec) ? Number(src.offsetSec) : 0,
                    range: src.range ? { inSec: Number(src.range.inSec ?? 0), outSec: Number(src.range.outSec ?? 0) } : undefined,
                    visual: src.visual
                        ? {
                              x: Number(src.visual.x ?? 0),
                              y: Number(src.visual.y ?? 0),
                              width: Number(src.visual.width ?? 0),
                              height: Number(src.visual.height ?? 0),
                              alpha: Number(src.visual.alpha ?? 1),
                              crop: src.visual.crop
                                  ? {
                                        x: Number(src.visual.crop.x ?? 0),
                                        y: Number(src.visual.crop.y ?? 0),
                                        width: Number(src.visual.crop.width ?? 1),
                                        height: Number(src.visual.crop.height ?? 1),
                                    }
                                  : undefined,
                          }
                        : undefined,
                    layerIndex: Number.isFinite(src.layerIndex) ? Number(src.layerIndex) : undefined,
                });
            }
            if (!inputConfigs.length) return;
            clip = ScriptLayerClip.fromInputConfigs(
                inputConfigs,
                { position: { x: comp.width / 2, y: comp.height / 2 }, width: comp.width, height: comp.height } as any,
                snap.id
            );

            // Apply edits after timing is restored.
            try {
                (clip as any).applyPersistedEdits?.({
                    scriptCuts: Array.isArray(snap.scriptCuts) ? snap.scriptCuts : [],
                    gaps: Array.isArray(snap.gaps) ? snap.gaps : [],
                    splits: Array.isArray(snap.splits) ? snap.splits : [],
                    speakerOverrides: Array.isArray(snap.speakerOverrides) ? snap.speakerOverrides : [],
                    speakerMuteRanges: Array.isArray(snap.speakerMuteRanges) ? snap.speakerMuteRanges : [],
                    transcriptHiddenRanges: Array.isArray(snap.transcriptHiddenRanges) ? snap.transcriptHiddenRanges : [],
                    wordCorrections: Array.isArray(snap.wordCorrections) ? snap.wordCorrections : [],
                    transcriptParagraphBreaks: Array.isArray(snap.transcriptParagraphBreaks) ? snap.transcriptParagraphBreaks : [],
                });
            } catch {}

            // Insert into script layer.
            // @ts-ignore
            await comp.insertClipIntoScriptLayer(clip, layerId || undefined);
        } else if (type === "image") {
            const assetId = String(snap.assetId ?? "");
            if (!assetId) return;
            const url = await this.resolveAssetUrl(assetId, "image");
            if (!url) return;
            const ref: MediaSourceRef = { id: assetId, kind: "image", imgUrl: url } as any;
            clip = new ImageClip(
                ref,
                { position: { x: comp.width / 2, y: comp.height / 2 }, width: comp.width, height: comp.height } as any,
                snap.id
            );
        } else if (type === "video" || type === "audio") {
            const assetId = String(snap.assetId ?? "");
            if (!assetId) return;
            const kind = type === "video" ? "video" : "audio";
            const url = await this.resolveAssetUrl(assetId, kind);
            if (!url) return;
            const source = new UrlSource(url);
            const input = new Input({ source, formats: ALL_FORMATS });
            const ref: MediaSourceRef = { id: assetId, kind, input } as any;
            clip =
                type === "video"
                    ? new VideoClip(
                          ref,
                          { position: { x: comp.width / 2, y: comp.height / 2 }, width: comp.width, height: comp.height } as any,
                          snap.id
                      )
                    : new AudioClip(ref, {}, snap.id);
        } else {
            return;
        }

        if (!clip) return;
        try { clip.name = snap.name ?? ""; } catch {}

        // Add to composition first so applySnapshot can find the live instance.
        if (type !== "script") {
            await comp.add(clip, layerId || undefined);
        }

        // Apply snapshot properties to match original.
        await this.applySnapshotToExistingClip(clip.id, snap);

        // Ensure asset mapping is restored.
        try { if (snap.assetId) CompositionStateStore.setAssetIdForClip(clip.id, snap.assetId); } catch {}
        try { if (snap.thumbnailId) CompositionStateStore.setThumbnailIdForClip(clip.id, snap.thumbnailId); } catch {}
        try { if (snap.name) CompositionStateStore.setTitleForClip(clip.id, snap.name); } catch {}
    }
}
